import { useState, useCallback, useMemo, useRef, useEffect } from 'react';

/**
 * Represents a selected cell in the table
 */
export interface SelectedCell {
  rowIndex: number;
  columnLabel: string;
}

/**
 * Options for cell selection behavior
 */
interface UseCellSelectionOptions {
  /**
   * If true, only allows selecting cells in the same column
   */
  sameColumnOnly?: boolean;
}

/**
 * Result of the useCellSelection hook
 */
interface UseCellSelectionResult {
  selectedCells: SelectedCell[];
  activeColumn: string | null;
  isValidSelection: boolean;
  isDragSelecting: boolean;
  selectCell: (cell: SelectedCell) => void;
  selectCells: (cells: SelectedCell[]) => void;
  toggleCell: (cell: SelectedCell) => void;
  clearSelection: () => void;
  handleCellClick: (cell: SelectedCell, event: { ctrlKey: boolean; metaKey: boolean }) => void;
  handleCellMouseDown: (cell: SelectedCell, event: CellSelectionMouseEvent) => void;
  handleCellMouseEnter: (cell: SelectedCell, event: CellSelectionMouseEvent) => void;
  handleCellMouseUp: () => void;
  consumePendingCellClick: () => boolean;
  isCellSelected: (rowIndex: number, columnLabel: string) => boolean;
}

interface CellSelectionMouseEvent {
  button?: number;
  buttons?: number;
  ctrlKey?: boolean;
  metaKey?: boolean;
  preventDefault?: () => void;
}

const getCellFromElement = (element: Element | null): SelectedCell | null => {
  const cellElement = element?.closest?.('[data-quick-fill-cell="true"]') as HTMLElement | null;
  if (!cellElement) {
    return null;
  }

  const rowIndex = Number(cellElement.dataset.rowIndex);
  const columnLabel = cellElement.dataset.columnLabel;

  if (!Number.isInteger(rowIndex) || !columnLabel) {
    return null;
  }

  return { rowIndex, columnLabel };
};

const buildColumnRangeSelection = (start: SelectedCell, end: SelectedCell): SelectedCell[] => {
  if (start.columnLabel !== end.columnLabel) {
    return [end];
  }

  const minRow = Math.min(start.rowIndex, end.rowIndex);
  const maxRow = Math.max(start.rowIndex, end.rowIndex);
  const cells: SelectedCell[] = [];

  for (let rowIndex = minRow; rowIndex <= maxRow; rowIndex++) {
    cells.push({ rowIndex, columnLabel: start.columnLabel });
  }

  return cells;
};

/**
 * Custom hook for managing cell selection in a table
 *
 * Separates cell selection business logic from UI components.
 * Handles multi-select with Ctrl/Cmd key, validates same-column selection,
 * and provides utilities for checking selection state.
 *
 * @param options - Configuration options for cell selection
 * @returns Cell selection state and methods
 */
export function useCellSelection(
  options: UseCellSelectionOptions = {}
): UseCellSelectionResult {
  const { sameColumnOnly = true } = options;

  const [selectedCells, setSelectedCells] = useState<SelectedCell[]>([]);
  const [isDragSelecting, setIsDragSelecting] = useState(false);
  const isDragSelectingRef = useRef(false);
  const dragStartCellRef = useRef<SelectedCell | null>(null);
  const hasDraggedSelectionRef = useRef(false);
  const suppressNextCellClickRef = useRef(false);

  /**
   * Get the active column (all cells should be in same column if sameColumnOnly is true)
   */
  const activeColumn = useMemo(() => {
    if (selectedCells.length === 0) return null;
    return selectedCells[0].columnLabel;
  }, [selectedCells]);

  /**
   * Check if selection is valid (all cells in same column)
   */
  const isValidSelection = useMemo(() => {
    if (selectedCells.length === 0) return false;
    if (!sameColumnOnly) return true;

    const firstColumn = selectedCells[0].columnLabel;
    return selectedCells.every(cell => cell.columnLabel === firstColumn);
  }, [selectedCells, sameColumnOnly]);

  const selectedCellKeys = useMemo(
    () => new Set(selectedCells.map((cell) => `${cell.rowIndex}||${cell.columnLabel}`)),
    [selectedCells]
  );

  /**
   * Check if a specific cell is selected
   */
  const isCellSelected = useCallback((rowIndex: number, columnLabel: string): boolean => {
    return selectedCellKeys.has(`${rowIndex}||${columnLabel}`);
  }, [selectedCellKeys]);

  /**
   * Select a single cell (replace current selection)
   */
  const selectCell = useCallback((cell: SelectedCell) => {
    setSelectedCells([cell]);
  }, []);

  const selectCells = useCallback((cells: SelectedCell[]) => {
    setSelectedCells(
      cells.filter(
        (cell) =>
          Number.isInteger(cell.rowIndex) &&
          cell.rowIndex >= 0 &&
          typeof cell.columnLabel === 'string' &&
          cell.columnLabel.trim() !== ''
      )
    );
  }, []);

  /**
   * Toggle a cell in the selection (add if not present, remove if present)
   */
  const toggleCell = useCallback((cell: SelectedCell) => {
    setSelectedCells(prev => {
      const isAlreadySelected = prev.some(
        c => c.rowIndex === cell.rowIndex && c.columnLabel === cell.columnLabel
      );

      if (isAlreadySelected) {
        // Remove from selection
        return prev.filter(
          c => !(c.rowIndex === cell.rowIndex && c.columnLabel === cell.columnLabel)
        );
      } else {
        // Add to selection, filter to same column if required
        const newSelection = [...prev, cell];

        if (sameColumnOnly && newSelection.length > 0) {
          const firstColumn = newSelection[0].columnLabel;
          return newSelection.filter(c => c.columnLabel === firstColumn);
        }

        return newSelection;
      }
    });
  }, [sameColumnOnly]);

  /**
   * Clear all cell selections
   */
  const clearSelection = useCallback(() => {
    setSelectedCells([]);
    dragStartCellRef.current = null;
    hasDraggedSelectionRef.current = false;
    suppressNextCellClickRef.current = false;
    isDragSelectingRef.current = false;
    setIsDragSelecting(false);
  }, []);

  const endDragSelection = useCallback(() => {
    if (hasDraggedSelectionRef.current) {
      suppressNextCellClickRef.current = true;
    }

    setIsDragSelecting(false);
    isDragSelectingRef.current = false;
  }, []);

  const updateDragSelection = useCallback((cell: SelectedCell) => {
    if (!isDragSelectingRef.current || !dragStartCellRef.current) {
      return;
    }

    hasDraggedSelectionRef.current = true;
    suppressNextCellClickRef.current = true;

    if (sameColumnOnly) {
      setSelectedCells(buildColumnRangeSelection(dragStartCellRef.current, cell));
      return;
    }

    setSelectedCells([dragStartCellRef.current, cell]);
  }, [sameColumnOnly]);

  useEffect(() => {
    if (!isDragSelecting) {
      return undefined;
    }

    const handleWindowMouseUp = () => endDragSelection();
    const handleWindowBlur = () => endDragSelection();
    const handleWindowMove = (event: MouseEvent | PointerEvent) => {
      if (!isDragSelectingRef.current) {
        return;
      }

      const pointerType = 'pointerType' in event ? event.pointerType : 'mouse';
      if (pointerType !== 'touch' && event.buttons !== undefined && (event.buttons & 1) !== 1) {
        endDragSelection();
        return;
      }

      const targetCell = getCellFromElement(document.elementFromPoint(event.clientX, event.clientY));
      if (targetCell) {
        updateDragSelection(targetCell);
      }
    };

    window.addEventListener('pointermove', handleWindowMove);
    window.addEventListener('pointerup', handleWindowMouseUp);
    window.addEventListener('pointercancel', handleWindowMouseUp);
    window.addEventListener('mousemove', handleWindowMove);
    window.addEventListener('mouseup', handleWindowMouseUp);
    window.addEventListener('blur', handleWindowBlur);

    return () => {
      window.removeEventListener('pointermove', handleWindowMove);
      window.removeEventListener('pointerup', handleWindowMouseUp);
      window.removeEventListener('pointercancel', handleWindowMouseUp);
      window.removeEventListener('mousemove', handleWindowMove);
      window.removeEventListener('mouseup', handleWindowMouseUp);
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, [endDragSelection, isDragSelecting, updateDragSelection]);

  /**
   * Handle cell click with Ctrl/Cmd key detection
   * Single click = select only that cell
   * Ctrl/Cmd+Click = toggle cell in selection
   */
  const handleCellClick = useCallback((
    cell: SelectedCell,
    event: { ctrlKey: boolean; metaKey: boolean }
  ) => {
    if (suppressNextCellClickRef.current) {
      suppressNextCellClickRef.current = false;
      return;
    }

    if (event.ctrlKey || event.metaKey) {
      toggleCell(cell);
    } else {
      selectCell(cell);
    }
  }, [toggleCell, selectCell]);

  const handleCellMouseDown = useCallback((
    cell: SelectedCell,
    event: CellSelectionMouseEvent
  ) => {
    if (event.button !== undefined && event.button !== 0) {
      return;
    }

    event.preventDefault?.();
    dragStartCellRef.current = cell;
    hasDraggedSelectionRef.current = false;
    suppressNextCellClickRef.current = false;
    isDragSelectingRef.current = true;
    setIsDragSelecting(true);

    if (event.ctrlKey || event.metaKey) {
      toggleCell(cell);
    } else {
      selectCell(cell);
    }
  }, [selectCell, toggleCell]);

  const handleCellMouseEnter = useCallback((
    cell: SelectedCell,
    event: CellSelectionMouseEvent
  ) => {
    if (!isDragSelectingRef.current || !dragStartCellRef.current) {
      return;
    }

    if (event.buttons !== undefined && (event.buttons & 1) !== 1) {
      endDragSelection();
      return;
    }

    updateDragSelection(cell);
  }, [endDragSelection, updateDragSelection]);

  const consumePendingCellClick = useCallback((): boolean => {
    if (!suppressNextCellClickRef.current) {
      return false;
    }

    suppressNextCellClickRef.current = false;
    return true;
  }, []);

  return {
    selectedCells,
    activeColumn,
    isValidSelection,
    isDragSelecting,
    selectCell,
    selectCells,
    toggleCell,
    clearSelection,
    handleCellClick,
    handleCellMouseDown,
    handleCellMouseEnter,
    handleCellMouseUp: endDragSelection,
    consumePendingCellClick,
    isCellSelected,
  };
}
